import { skipToken } from "@reduxjs/toolkit/query";
import { asRecordingId } from "@repo/app-types/entities/recording";
import { TeammateType } from "@repo/app-types/entities/teammate";
import { useMemo, useRef, type FC } from "react";
import { useParams } from "react-router";
import {
  useGetPlaybookTestResultQuery,
  useGetPlaybookTestRunQuery,
} from "../../api/entities/playbookTestSpec.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { TranscriptScrollPane } from "../../components/transcript/transcriptScrollPane.js";
import {
  GradingTranscriptWithIndex,
  type GradingTranscriptWithIndexHandle,
} from "../teammatePerformance/gradingTranscriptWithIndex.js";
import { TranscriptVideoPanel } from "../teammatePerformance/transcriptVideoPanel.js";
import { useTranscriptOutlineSync } from "../teammatePerformance/useTranscriptOutlineSync.js";

/**
 * Page accessible via /agents/:teammateAgentId/testing/specs/:testSpecId/runs/:testRunId/results/:testResultId/transcript
 */
export const TeammateAgentResultTranscript: FC = () => {
  const { teammateAgentId, testSpecId, testRunId, testResultId } = useParams();
  if (!teammateAgentId || !testSpecId || !testRunId || !testResultId) {
    throw new Error("Missing params.");
  }

  const { data: teammateAgent } = useGetTeammateAgentQuery(teammateAgentId);
  const playbookId = teammateAgent?.teammate_agent_config.playbook_id;

  const { data: playbookTestRun } = useGetPlaybookTestRunQuery(
    playbookId ? { playbookId, testSpecId, testRunId } : skipToken,
  );

  const { data: playbookTestResult } = useGetPlaybookTestResultQuery(
    playbookId ? { playbookId, testSpecId, testResultId } : skipToken,
  );

  const transcriptRef = useRef<GradingTranscriptWithIndexHandle>(null);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  // Transform test result data into recording format for transcript display.
  // The test result ID is reused as a fake recording ID to satisfy the transcript component's interface.
  const recordings = useMemo(
    () =>
      playbookTestRun && playbookTestResult?.data
        ? [
            {
              id: asRecordingId(playbookTestResult.id),
              title: "Test recording",
              duration: playbookTestResult.data.transcript.duration,
              recorded_at: playbookTestRun?.created_at,
              has_video: false,
              speakers: playbookTestResult.data.speakers,
              transcript: playbookTestResult.data.transcript,
              summary: null,
            },
          ]
        : [],
    [playbookTestRun, playbookTestResult],
  );
  const { scrollToTranscriptPosition } = useTranscriptOutlineSync({
    transcriptContainerRef,
    sortedRecordings: recordings,
  });

  // Build tool activities map from test result data
  const toolActivitiesByRecordingId = useMemo(() => {
    if (!playbookTestResult?.data?.tool_activities) {
      return undefined;
    }
    return {
      [playbookTestResult.id]: playbookTestResult.data.tool_activities,
    };
  }, [playbookTestResult]);

  return (
    <div className="grid h-full grid-rows-[minmax(0,1fr)_auto] overflow-hidden">
      <div className="flex min-h-0 flex-col bg-white">
        <div className="space-y-2 border-b border-border-light p-4">
          <h3 className="text-sm font-semibold text-foreground">
            Call transcript
          </h3>
        </div>
        <TranscriptVideoPanel recordings={recordings} />
        <TranscriptScrollPane
          scrollContainerRef={transcriptContainerRef}
          className="flex flex-col gap-4 p-4"
        >
          {recordings.length > 0 ? (
            <GradingTranscriptWithIndex
              ref={transcriptRef}
              recordings={recordings}
              teammateType={TeammateType.AGENT}
              quoteMatches={undefined}
              scrollToTranscriptPosition={scrollToTranscriptPosition}
              toolActivitiesByRecordingId={toolActivitiesByRecordingId}
            />
          ) : (
            <div className="flex items-center justify-center p-4 text-muted-foreground">
              No transcript available
            </div>
          )}
        </TranscriptScrollPane>
      </div>
    </div>
  );
};
